import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getPaymentMatchingRequest: ["payload"],
  getPaymentMatchingSuccess: ["payload"],
  getPaymentMatchingFailure: ["payload"],

  getPaymentMatchingSummaryRequest: ["payload"],
  getPaymentMatchingSummarySuccess: ["payload"],
  getPaymentMatchingSummaryFailure: ["payload"],

  getPaymentMatchingFilterOptionsRequest: ["payload"],
  getPaymentMatchingFilterOptionsSuccess: ["payload"],
  getPaymentMatchingFilterOptionsFailure: ["payload"],

  getPaymentMatchingLogsRequest: ["payload"],
  getPaymentMatchingLogsSuccess: ["payload"],
  getPaymentMatchingLogsFailure: ["payload"],

  getPaymentMatchingLogUsersRequest: ["payload"],
  getPaymentMatchingLogUsersSuccess: ["payload"],
  getPaymentMatchingLogUsersFailure: ["payload"],

  getPaymentMatchingGroupRequest: ["payload"],
  getPaymentMatchingGroupSuccess: ["payload"],
  getPaymentMatchingGroupFailure: ["payload"],

  getPaymentMatchingIconsRequest: ["payload"],
  getPaymentMatchingIconsSuccess: ["payload"],
  getPaymentMatchingIconsFailure: ["payload"],

  renamePaymentMatchingRequest: ["payload"],
  renamePaymentMatchingSuccess: ["payload"],
  renamePaymentMatchingFailure: ["payload"],

  setPaymentMatchingIconRequest: ["payload"],
  setPaymentMatchingIconSuccess: ["payload"],
  setPaymentMatchingIconFailure: ["payload"],

  uploadPaymentMatchingIconRequest: ["payload"],
  uploadPaymentMatchingIconSuccess: ["payload"],
  uploadPaymentMatchingIconFailure: ["payload"],

  deletePaymentMatchingIconRequest: ["payload"],
  deletePaymentMatchingIconSuccess: ["payload"],
  deletePaymentMatchingIconFailure: ["payload"],

  paymentMatchingSetProp: ["key", "value"],
  clearPaymentMatchingFilters: null,

  logout: null
})

export const PaymentMatchingTypes = Types
export default Creators

/* ------------- Constants ------------- */

// Exactly one block is always selected; there is no unselected state.
export const SUMMARY_BLOCKS = { ALL: "all", MAPPED: "mapped", UNMAPPED: "unmapped" }

// The multi-select dropdown uses "all" for "no restriction" and "no_value" for its explicit
// empty option, so both sentinels live here rather than being spelled out at every call site.
export const NO_RESTRICTION = "all"
export const NO_VALUE_OPTION = "no_value"

// What the API expects in place of a UI name to mean "rows that have none". The API advertises this
// value in its filter-options response and that answer wins; this default only covers the window
// before that response arrives, since the dropdown offers its empty option immediately.
export const NO_UI_NAME_FALLBACK = "__none__"

// Defaults of the log's three dropdowns. The date range is not All time on purpose: the log grows
// without limit and the common task is reviewing recent activity.
export const LOG_DEFAULT_FILTERS = { date_range: "last_30_days", field: "", user: "" }
export const LOG_PAGE_SIZE = 10

export const DEFAULT_ORDERING = "-transactions_count"
export const PAGE_SIZE = 25

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: { count: 0, next: null, previous: null, results: [] },

  summaryFetching: false,
  summary: {
    all_values: 0,
    mapped: 0,
    mapped_percent: 0,
    unmapped: 0,
    unmapped_percent: 0,
    counts_refreshed_at: null
  },

  filterOptions: { current_name: [], ui_name: [], payment_processor: [] },
  noUiNameValue: NO_UI_NAME_FALLBACK,

  block: SUMMARY_BLOCKS.ALL,
  filters: {
    current_name: NO_RESTRICTION,
    ui_name: NO_RESTRICTION,
    payment_processor: NO_RESTRICTION,
    no_icon: false
  },
  ordering: DEFAULT_ORDERING,
  page: 1,

  // The icon library only grows, so it is fetched once and kept for the popover's grid.
  iconLibrary: [],
  iconLibraryFetching: false,
  saveFetching: false,

  logs: { count: 0, results: [] },
  logsFetching: false,
  logUsers: []
})

/* ------------- Selectors ------------- */

export const selectHasActiveFilters = (state) => {
  const { block, filters } = state.paymentMatching

  return (
    block !== SUMMARY_BLOCKS.ALL ||
    filters.no_icon ||
    [filters.current_name, filters.ui_name, filters.payment_processor].some(
      (value) => Array.isArray(value) && value.length > 0
    )
  )
}

/* ------------- Reducers ------------- */

const getPaymentMatchingRequest = (state) => state.merge({ fetching: true })
const getPaymentMatchingSuccess = (state, { payload: result }) =>
  state.merge({ fetching: false, result })
const getPaymentMatchingFailure = (state) => state.merge({ fetching: false })

const getPaymentMatchingSummaryRequest = (state) => state.merge({ summaryFetching: true })
const getPaymentMatchingSummarySuccess = (state, { payload: summary }) =>
  state.merge({ summaryFetching: false, summary })
const getPaymentMatchingSummaryFailure = (state) => state.merge({ summaryFetching: false })

const getPaymentMatchingFilterOptionsSuccess = (state, { payload }) =>
  state.merge({
    filterOptions: {
      current_name: payload?.current_name || [],
      ui_name: payload?.ui_name || [],
      payment_processor: payload?.payment_processor || []
    },
    noUiNameValue: payload?.no_ui_name_value || NO_UI_NAME_FALLBACK
  })

const getPaymentMatchingLogsRequest = (state) => state.merge({ logsFetching: true })
const getPaymentMatchingLogsSuccess = (state, { payload }) =>
  state.merge({ logsFetching: false, logs: payload })
const getPaymentMatchingLogsFailure = (state) => state.merge({ logsFetching: false })

const getPaymentMatchingLogUsersSuccess = (state, { payload }) =>
  state.merge({ logUsers: payload || [] })

const getPaymentMatchingIconsRequest = (state) => state.merge({ iconLibraryFetching: true })
const getPaymentMatchingIconsSuccess = (state, { payload }) =>
  state.merge({ iconLibraryFetching: false, iconLibrary: payload || [] })
const getPaymentMatchingIconsFailure = (state) => state.merge({ iconLibraryFetching: false })

// Every edit shares one in-flight flag: only one row can be edited at a time, so there is never
// more than one of these running.
const editRequest = (state) => state.merge({ saveFetching: true })
const editSettled = (state) => state.merge({ saveFetching: false })

const paymentMatchingSetProp = (state, { key, value }) => {
  if (key === "filters") {
    // Any filter change returns the view to the first page, otherwise a narrower result set would
    // keep showing page 4 of a list that no longer has one.
    return state.merge({ filters: { ...state.filters, ...value }, page: 1 })
  }

  if (key === "block") {
    return state.merge({ block: value, page: 1 })
  }

  return state.merge({ [key]: value })
}

// Resets the filter bar and returns the summary block to All values, leaving sorting alone.
const clearPaymentMatchingFilters = (state) =>
  state.merge({ filters: INITIAL_STATE.filters, block: INITIAL_STATE.block, page: 1 })

const logout = () => INITIAL_STATE

/* ------------- Hookup ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_PAYMENT_MATCHING_REQUEST]: getPaymentMatchingRequest,
  [Types.GET_PAYMENT_MATCHING_SUCCESS]: getPaymentMatchingSuccess,
  [Types.GET_PAYMENT_MATCHING_FAILURE]: getPaymentMatchingFailure,

  [Types.GET_PAYMENT_MATCHING_SUMMARY_REQUEST]: getPaymentMatchingSummaryRequest,
  [Types.GET_PAYMENT_MATCHING_SUMMARY_SUCCESS]: getPaymentMatchingSummarySuccess,
  [Types.GET_PAYMENT_MATCHING_SUMMARY_FAILURE]: getPaymentMatchingSummaryFailure,

  [Types.GET_PAYMENT_MATCHING_FILTER_OPTIONS_SUCCESS]: getPaymentMatchingFilterOptionsSuccess,

  [Types.GET_PAYMENT_MATCHING_LOGS_REQUEST]: getPaymentMatchingLogsRequest,
  [Types.GET_PAYMENT_MATCHING_LOGS_SUCCESS]: getPaymentMatchingLogsSuccess,
  [Types.GET_PAYMENT_MATCHING_LOGS_FAILURE]: getPaymentMatchingLogsFailure,

  [Types.GET_PAYMENT_MATCHING_LOG_USERS_SUCCESS]: getPaymentMatchingLogUsersSuccess,

  [Types.GET_PAYMENT_MATCHING_ICONS_REQUEST]: getPaymentMatchingIconsRequest,
  [Types.GET_PAYMENT_MATCHING_ICONS_SUCCESS]: getPaymentMatchingIconsSuccess,
  [Types.GET_PAYMENT_MATCHING_ICONS_FAILURE]: getPaymentMatchingIconsFailure,

  [Types.RENAME_PAYMENT_MATCHING_REQUEST]: editRequest,
  [Types.RENAME_PAYMENT_MATCHING_SUCCESS]: editSettled,
  [Types.RENAME_PAYMENT_MATCHING_FAILURE]: editSettled,

  [Types.SET_PAYMENT_MATCHING_ICON_REQUEST]: editRequest,
  [Types.SET_PAYMENT_MATCHING_ICON_SUCCESS]: editSettled,
  [Types.SET_PAYMENT_MATCHING_ICON_FAILURE]: editSettled,

  [Types.UPLOAD_PAYMENT_MATCHING_ICON_REQUEST]: editRequest,
  [Types.UPLOAD_PAYMENT_MATCHING_ICON_SUCCESS]: editSettled,
  [Types.UPLOAD_PAYMENT_MATCHING_ICON_FAILURE]: editSettled,

  [Types.DELETE_PAYMENT_MATCHING_ICON_REQUEST]: editRequest,
  [Types.DELETE_PAYMENT_MATCHING_ICON_SUCCESS]: editSettled,
  [Types.DELETE_PAYMENT_MATCHING_ICON_FAILURE]: editSettled,

  [Types.PAYMENT_MATCHING_SET_PROP]: paymentMatchingSetProp,
  [Types.CLEAR_PAYMENT_MATCHING_FILTERS]: clearPaymentMatchingFilters,

  [Types.LOGOUT]: logout
})
